import { HbrInput } from '@harbor/elements-react-standalone';
import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  useCreatePovPrereqMutation,
  useUpdatePovPrereqMutation,
  useDeletePovPrereqMutation,
  useFetchPovPrereqs,
} from '../../../../../hooks/opportunity-pov-hooks';
import { OpportunityPoV } from '../../../../../types/opportunity-pov-items';
import StbButton from '../../../../security-toolbox-ui/StbButton';

interface PrerequisitesSectionProps {
  pov: OpportunityPoV;
  selectedProductId: number | null;
  onValidationChange: (isValid: boolean) => void;
  onLoadingChange: (isLoading: boolean) => void;
}

const style = {
  control: 'w-80 max-w-full min-h-20',
};

const PrerequisitesSection: React.FC<PrerequisitesSectionProps> = ({
  pov,
  selectedProductId,
  onValidationChange,
  onLoadingChange,
}) => {
  // Fetch prerequisites data
  const { data: povPreReqs, isLoading: loadingPrereqs } = useFetchPovPrereqs(pov?.id, { enabled: !!pov?.id });

  // CRUD mutations
  const { mutateAsync: createPovPrereq, isPending: isCreatingPovPrereq } = useCreatePovPrereqMutation();
  const { mutateAsync: updatePovPrereq, isPending: isUpdatingPovPrereq } = useUpdatePovPrereqMutation();
  const { mutateAsync: deletePovPrereq, isPending: isDeletingPovPrereq } = useDeletePovPrereqMutation();

  const isLoading = loadingPrereqs || isCreatingPovPrereq || isUpdatingPovPrereq || isDeletingPovPrereq;

  const isFormDirtyRef = useRef(false);
  const [isFormUpdated, setIsFormUpdated] = useState(false);
  const hasInitialized = useRef(false);
  const lastPovId = useRef<number | null>(null);
  const lastProductId = useRef<number | null>(null);

  // Track saved prerequisites to detect deletions
  const [savedPrereqs, setSavedPrereqs] = useState<{ id: number | null; value: string }[]>([]);

  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { dirtyFields },
  } = useForm({
    defaultValues: {
      prerequisites: [{ id: null, value: '' }],
    },
  });

  const { append, remove, fields } = useFieldArray({
    control,
    name: 'prerequisites',
  });

  // Report loading state to parent
  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  // Reset initialization flag when pov or product changes
  useEffect(() => {
    if (pov && pov.id !== lastPovId.current) {
      hasInitialized.current = false;
      lastPovId.current = pov.id;
    }
    if (selectedProductId && selectedProductId !== lastProductId.current) {
      hasInitialized.current = false;
      lastProductId.current = selectedProductId;
    }
  }, [pov, selectedProductId]);

  // Reset form ONLY on initial load, not on subsequent prereqs changes
  // This prevents form field flashing during updates
  useEffect(() => {
    if (!loadingPrereqs && povPreReqs !== undefined && selectedProductId && !hasInitialized.current) {
      const prereqs = (povPreReqs ?? [])
        .filter((prereq) => prereq.opportunity_product === selectedProductId)
        .map((prereq) => ({
          id: prereq.id,
          value: prereq.prereq,
        }));

      const initialPrereqs = prereqs.length > 0 ? prereqs : [{ id: null, value: '' }];
      
      reset({
        prerequisites: initialPrereqs,
      });
      
      setSavedPrereqs(initialPrereqs);
      hasInitialized.current = true;
    }
  }, [povPreReqs, selectedProductId, loadingPrereqs, reset]);

  // Separately sync prerequisites when data changes (without resetting entire form)
  useEffect(() => {
    if (!loadingPrereqs && povPreReqs && selectedProductId && hasInitialized.current) {
      const prereqs = povPreReqs
        .filter((prereq) => prereq.opportunity_product === selectedProductId)
        .map((prereq) => ({
          id: prereq.id,
          value: prereq.prereq,
        }));

      // Only update if we're not actively editing prereqs (prevents overwriting user input)
      if (!isFormDirtyRef.current && prereqs.length > 0) {
        setValue('prerequisites', prereqs, { shouldDirty: false });
        setSavedPrereqs(prereqs);
      }
    }
  }, [povPreReqs, selectedProductId, loadingPrereqs, setValue]);

  // Report validation state to parent
  useEffect(() => {
    const prerequisites = getValues('prerequisites');
    const hasValidPrereqs = prerequisites.some((prereq) => prereq.value.trim() !== '');
    onValidationChange(hasValidPrereqs);
  }, [getValues, onValidationChange]);

  // Save prerequisites when form updates
  useEffect(() => {
    const savePrereqs = async () => {
      if (!pov?.id || !selectedProductId) return;

      // Handle deletions - find prerequisites that were removed from the form
      const currentPrereqIds = getValues('prerequisites')
        .filter((p) => p.id !== null)
        .map((p) => p.id);
      
      const prereqsToDelete = savedPrereqs
        .filter((prereq) => prereq.id !== null && !currentPrereqIds.includes(prereq.id))
        .map((prereq) => prereq.id);

      if (prereqsToDelete.length > 0) {
        try {
          for (const id of prereqsToDelete) {
            await deletePovPrereq({ id });
          }
        } catch (error) {
          console.error('Error deleting prerequisite:', error);
        }
      }

      // Handle creates and updates
      if (isFormDirtyRef.current) {
        isFormDirtyRef.current = false;
        
        const prerequisites = getValues('prerequisites');
        for (const prereq of prerequisites) {
          if (prereq.value.trim() === '') continue; // skip empty prerequisites

          try {
            if (prereq.id) {
              // Update existing prerequisite
              const savedPrereq = povPreReqs?.find((p) => p.id === prereq.id);
              if (savedPrereq && prereq.value !== savedPrereq.prereq) {
                await updatePovPrereq({
                  id: prereq.id,
                  prereq: prereq.value,
                  pov: pov.id,
                  opportunity_product: selectedProductId,
                });
              }
            } else {
              // Create new prerequisite
              await createPovPrereq({
                prereq: prereq.value,
                pov: pov.id,
                opportunity_product: selectedProductId,
              });
            }
          } catch (error) {
            console.error('Error saving prerequisite:', error);
          }
        }

        // Update saved prerequisites after successful save
        const updatedPrereqs = prerequisites.filter((p) => p.value.trim() !== '');
        setSavedPrereqs(updatedPrereqs);
      }
    };

    if (isFormUpdated && isFormDirtyRef.current) {
      savePrereqs();
      setIsFormUpdated(false);
    }
  }, [
    isFormUpdated,
    pov,
    selectedProductId,
    povPreReqs,
    savedPrereqs,
    getValues,
    createPovPrereq,
    updatePovPrereq,
    deletePovPrereq,
  ]);

  return (
    <div className={style.control}>
      <label className='box-border text-[rgb(35,40,46)] text-[14.08px] font-semibold leading-[21.12px] pb-[2.08px]'>
        Prerequisites
      </label>
      {fields.map((field, i) => (
        <div key={field.id} className='flex justify-between items-center gap-1'>
          <Controller
            name={`prerequisites.${i}.value`}
            control={control}
            render={({ field: { ref, ...field } }) => (
              <HbrInput
                {...field}
                ref={ref}
                value={field.value}
                disabled={loadingPrereqs}
                onHbr-input={() => (isFormDirtyRef.current = true)}
                onHbr-change={(e) => {
                  field.onChange(e.target.value);
                  if (isFormDirtyRef.current) {
                    setIsFormUpdated(true);
                  }
                }}
                size='small'
                className='mb-1 w-full'
              />
            )}
          />

          <StbButton
            iconOnly={true}
            role='delete'
            size='small'
            variant='text'
            onClick={() => {
              remove(i);
              setIsFormUpdated(true);
              isFormDirtyRef.current = true;
            }}
          />
        </div>
      ))}

      <StbButton
        role='add'
        size='small'
        variant='text'
        onClick={() => append({ id: null, value: '' })}
        disabled={!!getValues('prerequisites').filter((p) => !p.value).length}
      >
        Add Prerequisite
      </StbButton>
    </div>
  );
};

export default PrerequisitesSection;
